
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';

const WhatIsSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-fast');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  // Scroll to team section when "Meet Your Experts" is clicked
  const scrollToTeam = () => {
    const teamSection = document.getElementById('team-section');
    if (teamSection) {
      teamSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} className="py-20 relative bg-gray-900">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-950/30"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
      <div className="absolute -top-10 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll will-change-transform">
            {t('whatIs.header')}
          </h2>
          
          <p className="text-3xl md:text-3x1 text-purple-400 mb-6 animate-on-scroll will-change-transform">
            {t('whatIs.subtitle')}
          </p>
          
          <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed animate-on-scroll will-change-transform">
            {t('whatIs.copy')}
          </p>
          
          <div className="hidden">
          <button 
            onClick={scrollToTeam}
            className="inline-flex items-center px-6 py-3 bg-transparent border border-purple-500 text-purple-400 rounded-full hover:bg-purple-500/10 transition-all duration-300 animate-on-scroll will-change-transform"
          >
            {t('whatIs.cta')}
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          </div>
        </div>
      </div>
      
      {/* Flowing lines decoration */}
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 116" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 50L48 45.3C96 40.7 192 31.3 288 30.7C384 30 480 38 576 45.3C672 52.7 768 59.3 864 64C960 68.7 1056 71.3 1152 64C1248 56.7 1344 39.3 1392 30.7L1440 22V116H1392C1344 116 1248 116 1152 116C1056 116 960 116 864 116C768 116 672 116 576 116C480 116 384 116 288 116C192 116 96 116 48 116H0V50Z" fill="url(#paint0_linear)"/>
        <defs>
          <linearGradient id="paint0_linear" x1="720" y1="22" x2="720" y2="116" gradientUnits="userSpaceOnUse">
            <stop stopColor="#4C1D95" stopOpacity="0.05"/>
            <stop offset="1" stopColor="#4C1D95" stopOpacity="0"/>
          </linearGradient>
        </defs>
      </svg>
    </section>
  );
};

export default WhatIsSection;
